import { useState, useEffect, useMemo, useCallback, useRef, type KeyboardEvent } from "react";
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Building2, Globe, Tag, Zap, AlertCircle, Loader2 } from "lucide-react";
import { getCustomerName } from "@/utils/customerLookup";
import * as tagsApi from "@/services/tagsApi";
import * as tenantsApi from "@/services/tenantsApi";
import { mapApiTag, MappedTag } from "@/utils/tagMapping";

/** Deterministic color palette for customer visual mapping */
const CUSTOMER_COLORS = [
  "#3B82F6", // blue
  "#10B981", // emerald
  "#F59E0B", // amber
  "#EF4444", // red
  "#8B5CF6", // violet
  "#EC4899", // pink
  "#06B6D4", // cyan
  "#84CC16", // lime
];

interface CustomerItem {
  id: string;
  name: string;
}

interface ScopeAndAssignStepProps {
  selectedCustomers: string[];
  onCustomersChange: (customers: string[]) => void;
  allCustomers: boolean;
  onAllCustomersChange: (allCustomers: boolean) => void;
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  payloadCount: number;
  platformCount: number;
  policyId?: string;
  /**
   * Hide the "All Customers" toggle. Used by wizards (App Config / PPPC) that
   * always require explicit customer + tag selection for external targets.
   * When hidden, `allCustomers` is forced to `false` internally.
   */
  hideAllCustomers?: boolean;
  /**
   * Hide the built-in "Deployment Summary" panel. Used when the host wizard
   * already renders its own summary card and a second one would duplicate.
   */
  hideSummary?: boolean;
  /**
   * Optional copy override for the page heading + subtitle. Lets host wizards
   * keep their domain-specific phrasing without forking the component.
   */
  heading?: string;
  subtitle?: string;
}

export function ScopeAndAssignStep({
  selectedCustomers,
  onCustomersChange,
  allCustomers,
  onAllCustomersChange,
  selectedTags,
  onTagsChange,
  payloadCount,
  platformCount,
  policyId,
  hideAllCustomers = false,
  hideSummary = false,
  heading,
  subtitle,
}: ScopeAndAssignStepProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [tagSearchQuery, setTagSearchQuery] = useState("");

  // Customers from API
  const [customers, setCustomers] = useState<CustomerItem[]>([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);

  // Tags from API
  const [allTags, setAllTags] = useState<MappedTag[]>([]);
  const [assignedTagIds, setAssignedTagIds] = useState<string[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [loadingCustomerTags, setLoadingCustomerTags] = useState<Record<string, boolean>>({});

  // Fetch customers from tenants API on mount
  useEffect(() => {
    const fetchCustomers = async () => {
      setIsLoadingCustomers(true);
      try {
        const tenants = await tenantsApi.listTenants();
        const mapped: CustomerItem[] = (tenants || []).map((t: any) => ({
          id: t.id || t.tenantId || "",
          name: t.name || t.tenantName || t.companyName || t.id || "Unknown",
        })).filter((c: CustomerItem) => c.id);
        setCustomers(mapped);
      } catch (error) {
        console.error("[ScopeAndAssign] Failed to fetch tenants:", error);
        setCustomers([]);
      } finally {
        setIsLoadingCustomers(false);
      }
    };
    fetchCustomers();
  }, []);

  // Fetch tags per selected customer (tenant-scoped) — sequential to avoid localStorage race
  useEffect(() => {
    if (selectedCustomers.length === 0) {
      setAllTags([]);
      setLoadingCustomerTags({});
      return;
    }
    let cancelled = false;
    const fetchTags = async () => {
      setIsLoadingTags(true);
      // Mark all selected customers as loading
      const loadingState: Record<string, boolean> = {};
      selectedCustomers.forEach(cid => { loadingState[cid] = true; });
      setLoadingCustomerTags(loadingState);
      // Keep already-fetched tags for customers still selected
      setAllTags(prev => prev.filter((t: any) => selectedCustomers.includes(t._customerId)));

      // Fetch sequentially to avoid localStorage tenant ID race condition
      for (const cid of selectedCustomers) {
        if (cancelled) break;
        try {
          const tags = await tagsApi.listTagsByTenant(cid);
          if (cancelled) break;
          // Filter tags client-side: only keep tags whose tenantId matches this customer
          const tenantFilteredTags = (tags || []).filter((t: any) => {
            const tagTenantId = t.tenantId || t.tenant_id;
            // If tag has no tenantId, include it (backwards compat); otherwise must match
            return !tagTenantId || tagTenantId === cid;
          });
          const mapped = tenantFilteredTags.map((t: any) => ({
            ...mapApiTag(t),
            _customerId: cid,
          }));
          setAllTags(prev => [
            ...prev.filter((t: any) => t._customerId !== cid),
            ...mapped,
          ]);
        } catch (error) {
          console.error(`[ScopeAndAssign] Failed to fetch tags for customer ${cid}:`, error);
        } finally {
          if (!cancelled) {
            setLoadingCustomerTags(prev => ({ ...prev, [cid]: false }));
          }
        }
      }
      if (!cancelled) setIsLoadingTags(false);
    };
    fetchTags();
    return () => { cancelled = true; };
  }, [selectedCustomers]);

  // Fetch already-assigned tags if editing
  useEffect(() => {
    if (!policyId) return;
    const fetchAssigned = async () => {
      try {
        const assigned = await tagsApi.getTagsByPolicy(policyId);
        const ids = (assigned || []).map((t: any) => t.tag_id || t.tagId || t.id || "").filter(Boolean);
        setAssignedTagIds(ids);
      } catch {
        setAssignedTagIds([]);
      }
    };
    fetchAssigned();
  }, [policyId]);

  // Build a stable color map: customerId → color (based on position in full list)
  const customerColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    customers.forEach((c, i) => {
      map[c.id] = CUSTOMER_COLORS[i % CUSTOMER_COLORS.length];
    });
    return map;
  }, [customers]);

  // Filter tags by search query
  const filteredTags = allTags.filter(tag =>
    tag.name.toLowerCase().includes(tagSearchQuery.toLowerCase())
  );

  // Group tags by the customer they were fetched from (_customerId)
  const tagsByCustomer = selectedCustomers.reduce<Record<string, (MappedTag & { _customerId?: string })[]>>((acc, cid) => {
    const customerTags = filteredTags.filter((tag: any) => tag._customerId === cid);
    if (customerTags.length > 0) {
      acc[cid] = customerTags;
    }
    return acc;
  }, {});

  // No unassociated tags needed — all tags now have a _customerId
  const unassociatedTags: MappedTag[] = [];

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ---------------------------------------------------------------------
  // Keyboard navigation for the tag combobox
  // ---------------------------------------------------------------------
  // Flat, render-ordered list of tag ids the user can highlight with
  // ArrowUp/ArrowDown. We rebuild this from the same `tagsByCustomer`
  // groups the JSX iterates so the visual order and the keyboard order
  // are guaranteed to match — no risk of "ArrowDown skips to a row that
  // isn't actually next on screen" when filters or customer selection
  // change.
  const navigableTagIds = useMemo<string[]>(() => {
    const ids: string[] = [];
    selectedCustomers.forEach((cid) => {
      (tagsByCustomer[cid] || []).forEach((t) => ids.push(t.id));
    });
    return ids;
  }, [selectedCustomers, tagsByCustomer]);

  const [activeTagId, setActiveTagId] = useState<string | null>(null);
  const tagRowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // If the current active id falls out of the filtered list (e.g. after
  // typing into the search box), drop the highlight rather than stranding
  // it on a hidden row.
  useEffect(() => {
    if (activeTagId && !navigableTagIds.includes(activeTagId)) {
      setActiveTagId(null);
    }
  }, [activeTagId, navigableTagIds]);

  // Keep the highlighted row in view when the user pages through with
  // arrows — checkboxes near the bottom of the ScrollArea would otherwise
  // stay hidden behind the overflow.
  useEffect(() => {
    if (!activeTagId) return;
    const el = tagRowRefs.current[activeTagId];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [activeTagId]);

  const handleCustomerToggle = (customerId: string, checked: boolean) => {
    if (checked) {
      onCustomersChange([...selectedCustomers, customerId]);
    } else {
      onCustomersChange(selectedCustomers.filter(id => id !== customerId));
      // Remove tags that belonged to the deselected customer
      const customerTagIds = allTags
        .filter((t: any) => t._customerId === customerId)
        .map(t => t.id);
      onTagsChange(selectedTags.filter(id => !customerTagIds.includes(id)));
    }
  };

  const handleAllCustomersToggle = (checked: boolean) => {
    onAllCustomersChange(checked);
    if (checked) {
      onCustomersChange([]);
      onTagsChange([]);
    }
  };

  const handleTagToggle = (tagId: string, checked: boolean) => {
    onTagsChange(checked ? [...selectedTags, tagId] : selectedTags.filter(id => id !== tagId));
  };

  /**
   * Keyboard handler attached to the tag search input. The search field is
   * the natural focus target — users land there first when reaching the
   * tag picker, and arrow keys would otherwise just move the caret inside
   * an already-empty single-line input.
   *
   *  - ArrowDown / ArrowUp: move highlight through `navigableTagIds`,
   *    wrapping at both ends. If nothing is highlighted yet, ArrowDown
   *    starts at index 0 and ArrowUp starts at the last item.
   *  - Enter: toggle the highlighted tag's selection. If no tag is
   *    highlighted but the list is non-empty, toggle the first one — this
   *    matches the common "type to filter, hit Enter to pick the top
   *    match" combobox idiom.
   *  - Home / End: jump to first / last navigable tag (cheap to add and
   *    expected by keyboard users; tests don't assert this but it costs
   *    nothing).
   *  - Escape: clear the highlight. Lets users abandon keyboard mode
   *    without having to blur the input.
   */
  const handleTagSearchKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (navigableTagIds.length === 0) return;

      const currentIndex = activeTagId
        ? navigableTagIds.indexOf(activeTagId)
        : -1;

      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          const next =
            currentIndex < 0 || currentIndex === navigableTagIds.length - 1
              ? 0
              : currentIndex + 1;
          setActiveTagId(navigableTagIds[next]);
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          const prev =
            currentIndex <= 0
              ? navigableTagIds.length - 1
              : currentIndex - 1;
          setActiveTagId(navigableTagIds[prev]);
          break;
        }
        case "Home": {
          e.preventDefault();
          setActiveTagId(navigableTagIds[0]);
          break;
        }
        case "End": {
          e.preventDefault();
          setActiveTagId(navigableTagIds[navigableTagIds.length - 1]);
          break;
        }
        case "Enter": {
          // Enter "filters" by virtue of the live-filter input AND, if a
          // row is highlighted (or the list is non-empty), selects it.
          // We always preventDefault so the wizard's outer form (if any)
          // doesn't accidentally submit on Enter while the user is
          // picking tags.
          e.preventDefault();
          const targetId =
            activeTagId && navigableTagIds.includes(activeTagId)
              ? activeTagId
              : navigableTagIds[0];
          if (!targetId) break;
          handleTagToggle(targetId, !selectedTags.includes(targetId));
          // Promote the just-toggled row to "active" so a follow-up
          // ArrowDown continues from a sensible position.
          setActiveTagId(targetId);
          break;
        }
        case "Escape": {
          if (activeTagId) {
            e.preventDefault();
            setActiveTagId(null);
          }
          break;
        }
        default:
          break;
      }
    },
    [activeTagId, navigableTagIds, selectedTags],
  );

  // When the host wizard hides the All-Customers switch, force the value to
  // false so downstream logic (tag picker visibility, save payload) is
  // consistent with what the user can actually see.
  const effectiveAllCustomers = hideAllCustomers ? false : allCustomers;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">{heading ?? "Scope & Assign"}</h2>
        <p className="text-muted-foreground">
          {subtitle ??
            "Select customers to scope available tags, then assign tags to deploy this configuration to matching devices"}
        </p>
      </div>

      {/* All Customers Toggle */}
      {!hideAllCustomers && (
        <GlassCard variant="default">
          <GlassCardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Globe className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <Label className="text-base font-medium">All Customers</Label>
                  <p className="text-sm text-muted-foreground">
                    Apply to all current and future customers
                  </p>
                </div>
              </div>
              <Switch checked={allCustomers} onCheckedChange={handleAllCustomersToggle} />
            </div>
          </GlassCardContent>
        </GlassCard>
      )}

      {!effectiveAllCustomers && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Customer Selection */}
          <GlassCard variant="default">
            <GlassCardHeader>
              <GlassCardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Customers
                {selectedCustomers.length > 0 && (
                  <Badge variant="secondary" className="ml-1">{selectedCustomers.length}</Badge>
                )}
              </GlassCardTitle>
            </GlassCardHeader>
            <GlassCardContent>
              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search customers..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <ScrollArea className="h-72">
                  <div className="space-y-1">
                    {isLoadingCustomers ? (
                      <>
                        {[1, 2, 3, 4].map(i => (
                          <Skeleton key={i} className="h-10 w-full" />
                        ))}
                      </>
                    ) : filteredCustomers.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">No customers found</p>
                    ) : (
                      filteredCustomers.map(customer => {
                        const color = customerColorMap[customer.id] || "#6b7280";
                        const isSelected = selectedCustomers.includes(customer.id);
                        return (
                          <div
                            key={customer.id}
                            className={`flex items-center space-x-3 p-2 rounded hover:bg-muted transition-colors ${
                              isSelected ? "bg-muted/50" : ""
                            }`}
                          >
                            <Checkbox
                              id={`scope-cust-${customer.id}`}
                              checked={isSelected}
                              onCheckedChange={(checked) => handleCustomerToggle(customer.id, !!checked)}
                            />
                            <span
                              className="w-3 h-3 rounded-full flex-shrink-0 ring-2 ring-background"
                              style={{ backgroundColor: color }}
                            />
                            <Label htmlFor={`scope-cust-${customer.id}`} className="cursor-pointer flex-1 text-sm">
                              {customer.name}
                            </Label>
                          </div>
                        );
                      })
                    )}
                  </div>
                </ScrollArea>
              </div>
            </GlassCardContent>
          </GlassCard>

          {/* Tags (filtered by selected customers) */}
          <GlassCard variant="default">
            <GlassCardHeader>
              <GlassCardTitle className="text-base flex items-center gap-2">
                <Tag className="h-4 w-4" />
                Tags
                {selectedTags.length > 0 && (
                  <Badge variant="secondary" className="ml-1">{selectedTags.length}</Badge>
                )}
              </GlassCardTitle>
            </GlassCardHeader>
            <GlassCardContent>
              {selectedCustomers.length === 0 ? (
                <div className="flex items-center justify-center h-72 text-muted-foreground">
                  <div className="text-center">
                    <Tag className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Select customers first to see available tags</p>
                  </div>
                </div>
              ) : isLoadingTags ? (
                <div className="space-y-3 py-4">
                  {[1, 2, 3].map(i => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search tags..."
                      value={tagSearchQuery}
                      onChange={(e) => setTagSearchQuery(e.target.value)}
                      onKeyDown={handleTagSearchKeyDown}
                      className="pl-9"
                      role="combobox"
                      aria-expanded={navigableTagIds.length > 0}
                      aria-autocomplete="list"
                      aria-controls="scope-tag-listbox"
                      aria-activedescendant={
                        activeTagId ? `scope-tag-row-${activeTagId}` : undefined
                      }
                      data-testid="tag-search-input"
                    />
                  </div>
                  <ScrollArea className="h-64">
                    <div
                      className="space-y-4"
                      role="listbox"
                      id="scope-tag-listbox"
                      aria-label="Tags"
                    >
                      {selectedCustomers.map((customerId) => {
                        const customerColor = customerColorMap[customerId] || "#6b7280";
                        const customerName = customers.find(c => c.id === customerId)?.name || getCustomerName(customerId);
                        const customerTags = tagsByCustomer[customerId] || [];
                        const isLoading = loadingCustomerTags[customerId];

                        return (
                          <div
                            key={customerId}
                            className="rounded-lg border border-border/50 overflow-hidden"
                            style={{ borderLeftWidth: "4px", borderLeftColor: customerColor }}
                          >
                            <div className="px-3 py-2 bg-muted/30 flex items-center gap-2">
                              <span
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: customerColor }}
                              />
                              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                {customerName}
                              </span>
                              {isLoading ? (
                                <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin text-muted-foreground" />
                              ) : (
                                <Badge variant="outline" className="ml-auto text-[10px]">
                                  {customerTags.length}
                                </Badge>
                              )}
                            </div>
                            {isLoading ? (
                              <div className="px-3 py-3 space-y-2">
                                <Skeleton className="h-8 w-full" />
                                <Skeleton className="h-8 w-3/4" />
                              </div>
                            ) : customerTags.length === 0 ? (
                              <div className="px-3 py-4 flex flex-col items-center text-center text-muted-foreground">
                                <Tag className="h-5 w-5 mb-1.5 opacity-40" />
                                <p className="text-xs font-medium">No tags available</p>
                                <p className="text-[11px] mt-0.5 opacity-75">Create tags for this customer to assign configurations</p>
                              </div>
                            ) : (
                              <div className="divide-y divide-border/30">
                                {customerTags.map(tag => (
                                  <div
                                    key={tag.id}
                                    ref={(el) => {
                                      tagRowRefs.current[tag.id] = el;
                                    }}
                                    id={`scope-tag-row-${tag.id}`}
                                    role="option"
                                    aria-selected={selectedTags.includes(tag.id)}
                                    data-active={activeTagId === tag.id}
                                    data-testid={`scope-tag-row-${tag.id}`}
                                    className={`flex items-center space-x-2 px-3 py-2 transition-colors ${
                                      activeTagId === tag.id
                                        ? "bg-accent"
                                        : "hover:bg-muted/20"
                                    }`}
                                  >
                                    <Checkbox
                                      id={`scope-tag-${tag.id}`}
                                      checked={selectedTags.includes(tag.id)}
                                      onCheckedChange={(checked) => handleTagToggle(tag.id, !!checked)}
                                    />
                                    <div className="flex-1 flex items-center justify-between min-w-0">
                                      <Label htmlFor={`scope-tag-${tag.id}`} className="cursor-pointer text-sm flex items-center gap-2 truncate">
                                        <span
                                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                          style={{ backgroundColor: customerColor }}
                                        />
                                        {tag.name}
                                      </Label>
                                      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                                        {assignedTagIds.includes(tag.id) && (
                                          <Badge variant="outline" className="text-[10px] px-1.5 border-primary/40 text-primary">Assigned</Badge>
                                        )}
                                        <Badge variant={tag.type === "dynamic" ? "secondary" : "outline"} className="text-[10px]">
                                          {tag.type}
                                        </Badge>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {unassociatedTags.length > 0 && (
                        <div className="rounded-lg border border-border/50 overflow-hidden border-l-4 border-l-muted-foreground/30">
                          <div className="px-3 py-2 bg-muted/30 flex items-center gap-2">
                            <Globe className="h-3 w-3 text-muted-foreground" />
                            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              Global Tags
                            </span>
                            <Badge variant="outline" className="ml-auto text-[10px]">
                              {unassociatedTags.length}
                            </Badge>
                          </div>
                          <div className="divide-y divide-border/30">
                            {unassociatedTags.map(tag => (
                              <div key={tag.id} className="flex items-center space-x-2 px-3 py-2 hover:bg-muted/20 transition-colors">
                                <Checkbox
                                  id={`scope-tag-${tag.id}`}
                                  checked={selectedTags.includes(tag.id)}
                                  onCheckedChange={(checked) => handleTagToggle(tag.id, !!checked)}
                                />
                                <div className="flex-1 flex items-center justify-between min-w-0">
                                  <Label htmlFor={`scope-tag-${tag.id}`} className="cursor-pointer text-sm flex items-center gap-2 truncate">
                                    <span
                                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                      style={{ backgroundColor: tag.color || "#6b7280" }}
                                    />
                                    {tag.name}
                                  </Label>
                                  <Badge variant={tag.type === "dynamic" ? "secondary" : "outline"} className="text-[10px]">
                                    {tag.type}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {filteredTags.length === 0 && (
                        <div
                          className="text-center py-8 text-muted-foreground"
                          data-testid="tag-picker-empty-state"
                          role="status"
                          aria-live="polite"
                        >
                          <Tag className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">
                            {tagSearchQuery.trim().length > 0
                              ? `No tags match "${tagSearchQuery}"`
                              : "No tags found for selected customers"}
                          </p>
                          {tagSearchQuery.trim().length > 0 && (
                            <p className="text-xs mt-1 opacity-75">
                              Try a different search term or clear the filter.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </GlassCardContent>
          </GlassCard>
        </div>
      )}

      {/* Deployment Impact */}
      {!hideSummary && (
        <GlassCard variant="strong" className="border-primary/20 bg-primary/5">
          <GlassCardHeader>
            <GlassCardTitle className="text-lg flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" />
              Deployment Summary
            </GlassCardTitle>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-2xl font-semibold mb-1">
                  {effectiveAllCustomers ? customers.length : selectedCustomers.length}
                </div>
                <div className="text-sm text-muted-foreground">Customers</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-semibold mb-1">{selectedTags.length}</div>
                <div className="text-sm text-muted-foreground">Tags assigned</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-semibold mb-1">{payloadCount}</div>
                <div className="text-sm text-muted-foreground">Payloads</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-semibold mb-1">{platformCount}</div>
                <div className="text-sm text-muted-foreground">Platforms</div>
              </div>
            </div>

            {selectedTags.length === 0 && !effectiveAllCustomers && (
              <div className="mt-4 p-3 bg-muted border rounded-lg flex items-center gap-2 text-muted-foreground">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm">
                  No tags selected — this will be saved as a draft. Assign tags to deploy to devices.
                </span>
              </div>
            )}
          </GlassCardContent>
        </GlassCard>
      )}
    </div>
  );
}
